import React, { useRef, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';

import { fg } from '@confluence/feature-gating';
import { AddCommentPromptButton } from '@confluence/comments-common/entry-points/AddCommentPromptButton.ts';
import { expValEquals } from '@confluence/feature-experiments';
import {
	ShortcutVisualizer,
	useShortcutStrings,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { UNIVERSAL_OPEN_COMMENT_EDITOR } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';

const i18n = defineMessages({
	addCommentTooltip: {
		id: 'object-comments.footer.add-button-tooltip',
		defaultMessage: 'Add comment',
		description:
			'Tooltip message for adding a comment, used in conjunction with shortcut visualizer',
	},
});

export const ObjectAddCommentPrompt = ({
	onClick,
	autofocusRef,
}: {
	onClick: () => void;
	autofocusRef: { current: boolean | null };
}) => {
	const { formatMessage } = useIntl();

	const addCommentShortcutStrings = useShortcutStrings({
		shortcut: UNIVERSAL_OPEN_COMMENT_EDITOR,
	});

	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (autofocusRef.current) {
			(buttonRef.current as HTMLElement)?.focus({
				preventScroll: true,
			});
			autofocusRef.current = false;
		}
	}, [autofocusRef]);

	// When the FG platform_tooltip_shortcuts_migration_1 is enabled, the shortcut is placed in the `shortcut` Tooltip prop
	const tooltipContent = fg('platform_tooltip_shortcuts_migration_1') ? (
		formatMessage(i18n.addCommentTooltip)
	) : (
		<ShortcutVisualizer
			shortcut={UNIVERSAL_OPEN_COMMENT_EDITOR}
			contentBefore={formatMessage(i18n.addCommentTooltip)}
			isTooltip
		/>
	);

	return (
		<Box>
			<Tooltip
				content={
					expValEquals('confluence_comments_cohiti_footer_tooltips', 'isEnabled', true)
						? tooltipContent
						: undefined
				}
				shortcut={
					fg('platform_tooltip_shortcuts_migration_1') ? addCommentShortcutStrings : undefined
				}
				position="top"
			>
				<AddCommentPromptButton
					onClick={onClick}
					autofocusRef={autofocusRef}
					testId="add-comment-button"
				/>
			</Tooltip>
		</Box>
	);
};
